import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Tag, Heart, Star, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { needsChoices, displayPrice } from '../lib/cartLine';
import PriceTag from './PriceTag';

const NEW_DAYS = 21;

/** Marketplace product tile. `rank` shows a best-seller number badge; `compact` is for horizontal rails. */
export default function ProductCard({ product, rank, compact = false }) {
  const { addToCart, loading } = useCart();
  const { user } = useAuth();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);

  const wishlisted = isWishlisted(product.id);
  const { now: price, was, pct: discount } = displayPrice(product);
  const tracked = product.track_inventory !== false;
  const soldOut = tracked && product.inventory <= 0;
  const lowStock = tracked && product.inventory > 0 && product.inventory <= 5;
  const isNew = product.created_at && (Date.now() - new Date(product.created_at).getTime()) < NEW_DAYS * 86400000;
  const chooser = needsChoices(product);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (chooser) { navigate(`/products/${product.id}`); return; }
    if (!user) { navigate('/auth'); return; }
    if (await addToCart(product.id)) {
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    await toggleWishlist(product.id);
  };

  return (
    <div className={`group relative bg-white rounded-2xl border border-ink/10 hover:border-honey/60 hover:shadow-[0_12px_30px_rgba(31,26,20,0.10)] transition-all flex flex-col h-full ${compact ? 'w-52 sm:w-56 flex-shrink-0' : ''}`}
      data-testid={`product-card-${product.id}`}>
      <Link to={`/products/${product.id}`} className="block">
        <div className="relative overflow-hidden aspect-square bg-sand rounded-t-2xl">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.title} loading="lazy"
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${soldOut ? 'opacity-50' : ''}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Tag className="w-12 h-12 text-ink/15" /></div>
          )}
          <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1">
            {rank && <span className="bg-ink text-white text-xs font-black px-2 py-1 rounded-full">#{rank} Best seller</span>}
            {discount && (
              <span className="bg-coral text-white text-xs font-black px-2 py-1 rounded-full shadow-sm" title={product.sale?.name || ''}>
                {discount}% OFF
              </span>
            )}
            {product.bundle && <span className="bg-honey text-ink text-xs font-black px-2 py-1 rounded-full">Bundle{product.bundle.savings > 0 ? ` · save $${product.bundle.savings.toFixed(0)}` : ''}</span>}
            {isNew && !discount && !rank && <span className="bg-white text-ink text-xs font-black px-2 py-1 rounded-full shadow-sm">NEW</span>}
          </div>
          <button onClick={handleWishlist} aria-label={wishlisted ? 'Remove from favorites' : 'Save to favorites'}
            data-testid={`wishlist-btn-${product.id}`}
            className={`absolute top-2.5 right-2.5 w-9 h-9 rounded-full flex items-center justify-center shadow-sm transition-all ${wishlisted ? 'bg-coral text-white' : 'bg-white/90 text-ink/60 hover:text-coral-600 hover:scale-110'}`}>
            <Heart className={`w-4 h-4 ${wishlisted ? 'fill-white' : ''}`} />
          </button>
          {soldOut && <span className="absolute bottom-2.5 left-2.5 bg-ink/80 text-white text-xs font-bold px-2 py-1 rounded-full">Sold out</span>}
          {lowStock && <span className="absolute bottom-2.5 left-2.5 bg-white text-coral-600 text-xs font-black px-2 py-1 rounded-full shadow-sm">Only {product.inventory} left!</span>}
        </div>
      </Link>

      <div className="p-3.5 flex flex-col flex-1">
        {product.category && <p className="text-[11px] text-ink/45 uppercase tracking-wider mb-0.5 truncate">{product.category}</p>}
        <Link to={`/products/${product.id}`}>
          <h3 className="font-semibold text-ink text-sm leading-snug line-clamp-2 hover:text-honey-700 transition-colors min-h-[2.5rem]">{product.title}</h3>
        </Link>
        <div className="flex items-center gap-1 mt-1 h-4">
          {product.review_count > 0 && (
            <>
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className={`w-3 h-3 ${i <= Math.round(product.avg_rating) ? 'fill-honey text-honey' : 'text-ink/15'}`} />
              ))}
              <span className="text-ink/50 text-xs ml-0.5">({product.review_count})</span>
            </>
          )}
        </div>
        <div className="mt-2 mb-3"><PriceTag now={price} was={was} className="text-[15px]" /></div>
        <button onClick={handleAddToCart} disabled={loading || soldOut}
          data-testid={`add-to-cart-${product.id}`}
          className={`mt-auto w-full flex items-center justify-center gap-2 text-sm font-bold py-2 rounded-full transition-all disabled:opacity-40 ${added ? 'bg-green-600 text-white' : 'bg-honey text-ink hover:bg-[#FFC21F]'}`}>
          {added ? <><Check className="w-4 h-4" /> Added</> : soldOut ? 'Sold out' : chooser ? 'Choose options' : <><ShoppingCart className="w-4 h-4" /> Add to cart</>}
        </button>
      </div>
    </div>
  );
}
